import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaSchool, FaCamera, FaMapMarkedAlt, FaMapMarkerAlt, FaSearch, FaTimes,
  FaChevronLeft, FaChevronRight, FaFilePdf, FaImage, FaLink, FaRecycle,
} from "react-icons/fa";
import { useGetEwastePublicStatsQuery, useGetEwastePublicSchoolsQuery } from "../redux/auth/authApi";
import { EWASTE_CATEGORIES, EWASTE_ITEM_LABEL, CO_CURRICULAR } from "../Components/SchoolAdmin/ewasteCatalog";
import ewasteBin from "../assets/images/E-Waste-bin.png";
import { SC6_CAMPAIGN, SC6_PATH, SC6_SHORT } from "../Components/SpecialCampaign6/sc6Config";
import "./SwachhataReports.css";
import "./EWasteReports.css";

/*
 * /special-campaign-6/e-waste-events (also the older /swachhata-hi-seva/e-waste-events)
 * — PUBLIC "E-Waste Collection Drive" page, part of Special Campaign 6
 * (no login): headline totals and a gallery of schools, each opening to its
 * report (item-wise quantities by the DARPG categories) and photographs. Built on the Solid Waste Management events page styles
 * (shr-) with its own tones (ewr-). Linked from the landing-page campaign
 * section, not from the announcement marquee.
 *
 * The state filter lives in the URL (?state=DELHI). Mixed text is always ONE
 * template string (the Anuvadini extension merges adjacent JSX text nodes).
 */

const CAT = Object.fromEntries(EWASTE_CATEGORIES.map((c) => [c.code, c]));
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const ccLabel = (k) => CO_CURRICULAR.find((c) => c.key === k)?.label || "";
const itemLabel = (it) => (it.item === "other" ? it.name || "Other" : EWASTE_ITEM_LABEL[`${it.category}:${it.item}`] || it.item);
const WAVE_PATH = `M0 30 ${Array.from({ length: 4 }, (_, k) => `C${k * 600 + 100} 0 ${k * 600 + 200} 0 ${k * 600 + 300} 30 C${k * 600 + 400} 60 ${k * 600 + 500} 60 ${k * 600 + 600} 30`).join(" ")} V80 H0Z`;


function StateSelect({ states, value, onChange }) {
  return (
    <label className="shr-select">
      <FaMapMarkedAlt aria-hidden="true" />
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Filter by state">
        <option value="">All states and UTs</option>
        {states.map((s) => <option key={s.key} value={s.key}>{`${s.name} (${fmt(s.count)})`}</option>)}
        {value && !states.some((s) => s.key === value) && <option value={value}>{titleCase(value)}</option>}
      </select>
      <svg className="shr-select__chev" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5" /></svg>
    </label>
  );
}

function StatusBox({ title, text, onRetry }) {
  return (
    <div className="shr-status">
      <span className="shr-status__icon" aria-hidden="true"><FaSchool /></span>
      <b>{title}</b>
      <span>{text}</span>
      {onRetry && <button type="button" className="shr-btn shr-btn--green" onClick={onRetry}>Try again</button>}
    </div>
  );
}

function SchoolCard({ row, index, onOpen }) {
  const cover = row.photos[0];
  const cats = EWASTE_CATEGORIES.filter((c) => row.totals?.[c.code]);
  return (
    <button type="button" className="shr-card shr-reveal" style={{ "--d": `${(index % 12) * 0.04}s` }} onClick={() => onOpen(row)}>
      <span className="shr-card__media">
        {cover ? <img src={cover.url} alt="" loading="lazy" /> : <span className="shr-card__nophoto" aria-hidden="true"><FaImage /></span>}
        <span className="shr-card__count"><FaCamera aria-hidden="true" />{`${row.photos.length}`}</span>
        <span className="shr-card__view">View report</span>
      </span>
      <span className="shr-card__body">
        <span className="shr-card__name">{row.school_name}</span>
        <span className="shr-card__place"><FaMapMarkerAlt aria-hidden="true" />{[row.district, row.state].filter(Boolean).join(", ")}</span>
        {cats.length > 0 ? (
          <span className="shr-card__tags ewr-card-cats">
            {cats.map((c) => <em key={c.code} className="ewr-code" style={{ "--tone": c.tone }}>{`${c.code} ${fmt(row.totals[c.code])}`}</em>)}
          </span>
        ) : (
          <span className="shr-card__tags"><em>Awareness activity</em></span>
        )}
        <span className="shr-card__foot">
          <span>{row.collected === "yes" ? `${fmt(row.totals?.items)} items collected` : `${fmt(row.participants?.total)} people took part`}</span>
          <span>{fmtDate(row.submittedAt)}</span>
        </span>
      </span>
    </button>
  );
}

function ReportModal({ row, onClose }) {
  const images = row.photos;
  const [active, setActive] = useState(0);
  const closeRef = useRef(null);
  const go = useCallback((d) => setActive((i) => (images.length ? (i + d + images.length) % images.length : 0)), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, go]);

  const place = [row.block, row.district, row.state].filter(Boolean).join(", ");
  const pill = (v, text) => <span className={`shr-pill shr-pill--${v || "na"}`}>{text}</span>;
  const byCat = EWASTE_CATEGORIES.map((c) => ({ c, items: row.items.filter((i) => i.category === c.code) })).filter((g) => g.items.length);

  return (
    <div className="shr-modal" role="dialog" aria-modal="true" aria-label={`${row.school_name} E-Waste report`} onClick={onClose}>
      <div className="shr-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="shr-dialog__head">
          <span className="shr-dialog__icon ewr-dialog-icon" aria-hidden="true"><FaRecycle /></span>
          <div className="shr-dialog__titles">
            <h2 className="shr-dialog__title">{row.school_name}</h2>
            <div className="shr-dialog__meta">
              {place && <span><FaMapMarkerAlt aria-hidden="true" />{place}</span>}
              <span>{`UDISE ${row.udise}`}</span>
              {row.submittedAt && <span>{`Shared on ${fmtDate(row.submittedAt)}`}</span>}
            </div>
          </div>
          <button ref={closeRef} type="button" className="shr-dialog__close" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </div>

        <div className="shr-dialog__body">
          <div className="shr-gallery">
            <span className="shr-gallery__label">{`Photographs of the drive (${images.length})`}</span>
            {images.length ? (
              <div className="shr-stage">
                <img key={images[active].url} src={images[active].url} alt={`${row.school_name}, photograph ${active + 1}`} />
                {images.length > 1 && (
                  <>
                    <button type="button" className="shr-stage__nav shr-stage__nav--prev" onClick={() => go(-1)} aria-label="Previous photograph"><FaChevronLeft /></button>
                    <button type="button" className="shr-stage__nav shr-stage__nav--next" onClick={() => go(1)} aria-label="Next photograph"><FaChevronRight /></button>
                    <span className="shr-stage__count">{`${active + 1} / ${images.length}`}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="shr-stage shr-stage--empty"><FaImage aria-hidden="true" /><span>No photographs</span></div>
            )}
            {(images.length > 1 || row.document) && (
              <div className="shr-thumbs">
                {images.map((p, k) => (
                  <button key={p.url} type="button" className={`shr-thumb ${k === active ? "is-on" : ""}`} onClick={() => setActive(k)} aria-label={`Photograph ${k + 1}`}><img src={p.url} alt="" /></button>
                ))}
                {row.document && (
                  <a className="shr-thumb shr-thumb--pdf" href={row.document.url} target="_blank" rel="noopener noreferrer" aria-label="Open the receipt / certificate">
                    {row.document.type === "pdf" ? <FaFilePdf aria-hidden="true" /> : <FaImage aria-hidden="true" />}
                    <span>Receipt</span>
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="shr-answers">
            <div className="shr-answer">
              <div className="shr-answer__q"><span>1</span><p>The activity</p></div>
              <div className="shr-answer__a ewr-activity">
                <span className="shr-chip">{`Held on ${fmtDate(row.activityDate)}`}</span>
                <span className="shr-chip">{`${fmt(row.workshops)} workshop${row.workshops === 1 ? "" : "s"}`}</span>
                {row.coCurricular && <span className="shr-chip">{ccLabel(row.coCurricular)}</span>}
                {row.description && <p className="ewr-desc">{row.description}</p>}
              </div>
            </div>
            <div className="shr-answer">
              <div className="shr-answer__q"><span>2</span><p>People who took part</p></div>
              <div className="shr-answer__a">
                <div className="shr-mini-people">
                  {[["Students", row.participants.students], ["Teachers", row.participants.teachers], ["Total", row.participants.total]].map(([label, v]) => (
                    <span key={label}><b>{fmt(v)}</b><small>{label}</small></span>
                  ))}
                </div>
              </div>
            </div>
            <div className="shr-answer">
              <div className="shr-answer__q"><span>3</span><p>E-Waste collection corner / bin set up</p></div>
              <div className="shr-answer__a">{pill(row.collectionCorner, { yes: "Yes", no: "No", na: "Not applicable" }[row.collectionCorner] || "—")}</div>
            </div>
            <div className="shr-answer">
              <div className="shr-answer__q"><span>4</span><p>{row.collected === "yes" ? `E-Waste collected · ${fmt(row.totals?.items)} items` : "E-Waste collected"}</p></div>
              <div className="shr-answer__a">
                {row.collected !== "yes" ? pill("no", "No items collected") : (
                  <div className="ewr-itemgroups">
                    {byCat.map(({ c, items }) => (
                      <div key={c.code} className="ewr-itemgroup">
                        <div className="ewr-itemgroup__head">
                          <em className="ewr-code" style={{ "--tone": c.tone }}>{c.code}</em>
                          <span>{c.title}</span>
                          <b>{fmt(row.totals?.[c.code])}</b>
                        </div>
                        <div className="ewr-itemgroup__chips">
                          {items.map((it) => <span key={`${it.item}-${it.name}`} className="shr-chip">{`${itemLabel(it)} × ${fmt(it.qty)}`}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EWasteReports() {
  const [params, setParams] = useSearchParams();
  const state = String(params.get("state") || "").toUpperCase();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const [copied, setCopied] = useState(false);
  const [armed, setArmed] = useState(false);
  const rootRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    document.title = "E-Waste Collection Drive · Special Campaign 6 — Eco Clubs for Mission LiFE";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const filterKey = `${state}|${search}`;
  const [view, setView] = useState({ key: filterKey, page: 1 });
  const page = view.key === filterKey ? view.page : 1;

  const statsQ = useGetEwastePublicStatsQuery({ state });
  const listQ = useGetEwastePublicSchoolsQuery({ state, search, page });
  const [acc, setAcc] = useState({ key: "", rows: [], total: 0, pages: 1, page: 0 });
  const cur = listQ.currentData;
  useEffect(() => {
    if (!cur?.success) return;
    setAcc((prev) => {
      const base = prev.key === filterKey && cur.page > 1 ? prev.rows : [];
      const seen = new Set(base.map((r) => r.id));
      return { key: filterKey, rows: [...base, ...cur.rows.filter((r) => !seen.has(r.id))], total: cur.total, pages: cur.pages, page: cur.page };
    });
  }, [cur, filterKey]);

  const sameFilter = acc.key === filterKey;
  const rows = useMemo(() => (sameFilter ? acc.rows : []), [sameFilter, acc.rows]);
  const hasMore = sameFilter && acc.page < acc.pages;
  const loadingMore = sameFilter && listQ.isFetching && page > acc.page;

  const stats = statsQ.data?.success ? statsQ.data : null;
  const scopeName = stats?.scope?.stateName || (state ? titleCase(state) : "");

  const collage = useMemo(() => rows.filter((r) => r.photos.length).map((r) => ({ row: r, photo: r.photos[0] })).slice(0, 8), [rows]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined" || reducedMotion()) return undefined;
    setArmed(true);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.setAttribute("data-shown", "");
          io.unobserve(en.target);
        }
      }),
      { threshold: 0.06, rootMargin: "0px 0px -30px 0px" }
    );
    root.querySelectorAll(".shr-reveal:not([data-shown])").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [stats, rows.length]);

  const pickState = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("state", v);
    else next.delete("state");
    setParams(next, { replace: true });
  };
  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      const t = document.createElement("textarea");
      t.value = url;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const toGallery = () => galleryRef.current?.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
  const closeModal = useCallback(() => setOpenRow(null), []);

  const listLead = !sameFilter
    ? "Loading schools…"
    : `${fmt(acc.total)} ${acc.total === 1 ? "school" : "schools"}${scopeName ? ` in ${scopeName}` : ""}${search ? ` matching “${search}”` : ""}. Open a school to see what it collected and its photographs.`;

  return (
    <div ref={rootRef} className={`shr-page ewr ${armed ? "is-armed" : ""}`}>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="shr-hero">
        <span className="shr-hero__stripe" aria-hidden="true" />
        <div className="shr-wrap shr-hero__grid">
          <div className="shr-hero__copy">
            <span className="shr-hero__badge"><i aria-hidden="true" />{SC6_CAMPAIGN}</span>
            <h1 className="shr-hero__title">
              <span>E-Waste Collection Drive</span>
              <span className="shr-hero__year">2026</span>
            </h1>
            <p className="shr-hero__subtitle">E-Waste collected by schools across India</p>
            <p className="shr-hero__sub">
              Old computers, batteries, tube lights, lab instruments and more — counted item by item under the categories of the E-Waste (Management) Rules, 2022, with photographs from every school.
            </p>
            <div className="shr-hero__actions">
              <button type="button" className="shr-btn shr-btn--light" onClick={toGallery}>
                <FaImage aria-hidden="true" />
                <span>Browse school reports</span>
              </button>
              <Link to={SC6_PATH} className="shr-btn shr-btn--ghost">
                <span>{`${SC6_SHORT} · Upload participation details`}</span>
              </Link>
              <button type="button" className="shr-btn shr-btn--ghost" onClick={copyLink}>
                <FaLink aria-hidden="true" />
                <span>{copied ? "Link copied" : "Copy link"}</span>
              </button>
            </div>
          </div>
          <div className="shr-hero__art" aria-hidden="true">
            {collage.slice(0, 3).map((c, k) => (
              <span key={c.row.id} className={`shr-polaroid shr-polaroid--${k}`}><img src={c.photo.url} alt="" /></span>
            ))}
            <span className="ewr-hero-bin"><img src={ewasteBin} alt="" /></span>
          </div>
        </div>
        <svg className="shr-wave shr-wave--back" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true"><path d={WAVE_PATH} /></svg>
        <svg className="shr-wave" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true"><path d={WAVE_PATH} /></svg>
      </div>

      <div className="shr-wrap">
        {/* ── Gallery ────────────────────────────────────────────────────── */}
        <section className="shr-section shr-section--gallery" ref={galleryRef} aria-labelledby="ewr-gallery-title">
          <div className="shr-section__head shr-reveal">
            <div>
              <span className="shr-kicker">Gallery</span>
              <h2 id="ewr-gallery-title" className="shr-h2">E-Waste drives in schools</h2>
              <p className="shr-section__lead">{listLead}</p>
            </div>
            <div className="shr-toolbar">
              <label className="shr-search">
                <FaSearch aria-hidden="true" />
                <input type="search" placeholder="Search by school name or UDISE" value={searchText} onChange={(e) => setSearchText(e.target.value)} aria-label="Search schools" />
              </label>
              <StateSelect states={stats?.states || []} value={state} onChange={pickState} />
            </div>
          </div>

          {listQ.isError && !rows.length ? (
            <StatusBox title="Could not load the schools" text="Please check your connection and try again." onRetry={listQ.refetch} />
          ) : !sameFilter ? (
            <div className="shr-grid">{Array.from({ length: 8 }, (_, k) => <span key={k} className="shr-skel shr-skel--card" />)}</div>
          ) : rows.length === 0 ? (
            <StatusBox title={search ? "No school matches your search" : "No E-Waste drives shared yet"} text={search ? "Try another school name or UDISE code." : "Reports will appear here as schools share them."} />
          ) : (
            <div className="shr-grid">{rows.map((r, i) => <SchoolCard key={r.id} row={r} index={i} onOpen={setOpenRow} />)}</div>
          )}

          {hasMore && (
            <div className="shr-more">
              <button type="button" className="shr-btn shr-btn--green" disabled={loadingMore} onClick={() => setView({ key: filterKey, page: acc.page + 1 })}>
                {loadingMore ? "Loading…" : `Show more schools (${fmt(acc.total - rows.length)} more)`}
              </button>
            </div>
          )}
        </section>
      </div>

      {openRow && <ReportModal row={openRow} onClose={closeModal} />}
    </div>
  );
}
